import type { MappingReferenceData } from 'services/reference-generator/types';
import { isEditableMappingReference, mappingRowStatus } from './mappingReferenceStatus';

const cellRefKey = (ref: MappingReferenceData['cell_references'][number]): string =>
  `${ref.ref}:${ref.direction ?? ''}`;

/** Identity of one Excel mapping so duplicates collapse without losing distinct sources. */
export const excelReferenceIdentity = (ref: MappingReferenceData): string =>
  `${ref.document_reference}|${ref.sheet_name}|${(ref.cell_references ?? []).map(cellRefKey).join(',')}`;

/**
 * Collapse same-source duplicates within one kind, keeping each row's place. Editable rows:
 * the first seen stays. Review items: every pending proposal stays, since each is a stored row
 * waiting for its own review; a dismissed row stays only when no pending row shares its source.
 * An editable row and a review item of the same source both stay.
 */
export const repairExcelReferences = (refs: MappingReferenceData[] | undefined): MappingReferenceData[] => {
  if (!refs?.length) {
    return [];
  }
  const pendingSources = new Set(refs.filter(ref => mappingRowStatus(ref) === 'pending').map(excelReferenceIdentity));
  const seen = new Set<string>();
  return refs.filter(ref => {
    if (mappingRowStatus(ref) === 'pending') {
      return true;
    }
    const editable = isEditableMappingReference(ref);
    const identity = excelReferenceIdentity(ref);
    const key = `${editable}|${identity}`;
    if (seen.has(key) || (!editable && pendingSources.has(identity))) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
